/**
 * Email Parser Service
 * Parses provider email responses with confidence scoring
 * Includes multi-step parsing: structured → attachments → regex
 */

export interface ParsedConfirmation {
  available: boolean;
  confirmedRooms?: number;
  totalPrice?: number;
  currency?: string;
  confirmationNumber?: string;
  checkInInstructions?: string;
  confidence: number; // 0-1
  parserVersion: string;
  rawContent: string;
  parsedFields: Record<string, any>;
  requiresManualReview?: boolean;
}

class EmailParserService {
  private readonly CONFIDENCE_THRESHOLD = 0.85;

  /**
   * Parse provider email response
   * Multi-step approach: structured → attachments → regex
   */
  async parseProviderResponse(
    emailContent: string,
    attachments?: Array<{ filename: string; content: string; mimeType: string }>
  ): Promise<ParsedConfirmation> {
    let confidence = 0;
    const parsedFields: Record<string, any> = {};
    const rawContent = emailContent;

    // Step 1: Check for structured content (JSON, iCal, XML)
    const structuredData = await this.extractStructuredContent(emailContent, attachments);
    if (structuredData) {
      confidence = 0.95;
      return {
        available: structuredData.available ?? false,
        confirmedRooms: structuredData.confirmedRooms,
        totalPrice: structuredData.totalPrice,
        currency: structuredData.currency,
        confirmationNumber: structuredData.confirmationNumber,
        checkInInstructions: structuredData.checkInInstructions,
        confidence,
        parserVersion: 'structured_v1',
        rawContent,
        parsedFields: structuredData
      };
    }

    // Step 2: Extract from attachments (PDF, Word, etc.)
    if (attachments && attachments.length > 0) {
      const attachmentText = await this.extractTextFromAttachments(attachments);
      if (attachmentText) {
        const attachmentParsed = await this.parseEmailBody(attachmentText);
        confidence = Math.max(confidence, attachmentParsed.confidence);
        Object.assign(parsedFields, attachmentParsed.fields);
      }
    }

    // Step 3: Parse email body with regex rules
    const emailParsed = await this.parseEmailBody(emailContent);
    confidence = Math.max(confidence, emailParsed.confidence);
    Object.assign(parsedFields, emailParsed.fields);

    // Step 4: Validate parsed data
    const validation = this.validateParsedData(parsedFields);
    if (!validation.valid) {
      confidence *= 0.7; // Reduce confidence if validation fails
    }

    // Step 5: Confidence threshold check
    if (confidence < this.CONFIDENCE_THRESHOLD) {
      return {
        available: false,
        confidence,
        parserVersion: 'multi_step_v1',
        rawContent,
        parsedFields,
        requiresManualReview: true
      };
    }

    return {
      available: parsedFields.available ?? false,
      confirmedRooms: parsedFields.confirmedRooms,
      totalPrice: parsedFields.totalPrice,
      currency: parsedFields.currency || 'KES',
      confirmationNumber: parsedFields.confirmationNumber,
      checkInInstructions: parsedFields.checkInInstructions,
      confidence,
      parserVersion: 'multi_step_v1',
      rawContent,
      parsedFields
    };
  }

  /**
   * Extract structured content (JSON, iCal, XML)
   */
  private async extractStructuredContent(
    emailContent: string,
    attachments?: Array<{ filename: string; content: string; mimeType: string }>
  ): Promise<Partial<ParsedConfirmation> | null> {
    // Check attachments for structured formats
    if (attachments) {
      for (const attachment of attachments) {
        if (attachment.mimeType === 'application/json') {
          try {
            const data = JSON.parse(attachment.content);
            return {
              available: data.available ?? data.confirmed ?? false,
              confirmedRooms: data.confirmedRooms ?? data.rooms,
              totalPrice: data.totalPrice ?? data.price,
              currency: data.currency,
              confirmationNumber: data.confirmationNumber ?? data.confirmation ?? data.ref,
              checkInInstructions: data.checkInInstructions
            };
          } catch (e) {
            // Not valid JSON
          }
        }
      }
    }

    // Check email body for JSON
    const jsonMatch = emailContent.match(/\{[\s\S]*"available"[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const data = JSON.parse(jsonMatch[0]);
        return {
          available: data.available ?? false,
          confirmedRooms: data.confirmedRooms,
          totalPrice: data.totalPrice,
          currency: data.currency,
          confirmationNumber: data.confirmationNumber
        };
      } catch (e) {
        // Not valid JSON
      }
    }

    return null;
  }

  /**
   * Extract text from attachments (PDF, Word, etc.)
   * In production, use OCR or document parsing libraries
   */
  private async extractTextFromAttachments(
    attachments: Array<{ filename: string; content: string; mimeType: string }>
  ): Promise<string | null> {
    // For now, return null - in production, implement OCR for PDFs
    // and text extraction for Word documents
    return null;
  }

  /**
   * Parse email body with regex patterns
   */
  private async parseEmailBody(emailContent: string): Promise<{ confidence: number; fields: Record<string, any> }> {
    const fields: Record<string, any> = {};
    let confidence = 0.5; // Base confidence

    // Normalize text
    const normalized = emailContent.toLowerCase().replace(/\s+/g, ' ');

    // Check for availability keywords
    const availablePatterns = [
      /(?:confirmed|available|accepted|approved|booked|reserved)/i,
      /(?:yes|ok|agreed|confirmed)/i
    ];
    const unavailablePatterns = [
      /(?:not\s+available|unavailable|rejected|declined|full|booked\s+out)/i,
      /(?:no|sorry|cannot|unable)/i
    ];

    let availableFound = false;
    let unavailableFound = false;

    for (const pattern of availablePatterns) {
      if (pattern.test(emailContent)) {
        availableFound = true;
        confidence += 0.2;
        break;
      }
    }

    for (const pattern of unavailablePatterns) {
      if (pattern.test(emailContent)) {
        unavailableFound = true;
        confidence += 0.1;
        break;
      }
    }

    if (availableFound && !unavailableFound) {
      fields.available = true;
    } else if (unavailableFound && !availableFound) {
      fields.available = false;
    }

    // Extract confirmation number
    const confirmationPatterns = [
      /confirmation\s*(?:number|#|no\.?):\s*([A-Z0-9\-]+)/i,
      /ref(?:erence)?\s*(?:number|#|no\.?):\s*([A-Z0-9\-]+)/i,
      /booking\s*(?:id|number|#):\s*([A-Z0-9\-]+)/i,
      /conf\s*#?\s*:?\s*([A-Z0-9\-]+)/i
    ];

    for (const pattern of confirmationPatterns) {
      const match = emailContent.match(pattern);
      if (match && match[1]) {
        fields.confirmationNumber = match[1].trim();
        confidence += 0.15;
        break;
      }
    }

    // Extract price
    const pricePatterns = [
      /total\s*(?:price|amount|cost):\s*([\d,]+\.?\d*)\s*([A-Z]{3})?/i,
      /price:\s*([\d,]+\.?\d*)\s*([A-Z]{3})?/i,
      /amount:\s*([\d,]+\.?\d*)\s*([A-Z]{3})?/i,
      /kes\s*([\d,]+\.?\d*)/i,
      /usd\s*\$?\s*([\d,]+\.?\d*)/i
    ];

    for (const pattern of pricePatterns) {
      const match = emailContent.match(pattern);
      if (match && match[1]) {
        fields.totalPrice = parseFloat(match[1].replace(/,/g, ''));
        if (match[2]) {
          fields.currency = match[2].toUpperCase();
        } else if (pattern.source.includes('kes')) {
          fields.currency = 'KES';
        } else if (pattern.source.includes('usd')) {
          fields.currency = 'USD';
        }
        confidence += 0.1;
        break;
      }
    }

    // Extract confirmed rooms
    const roomsPatterns = [
      /(?:confirmed|available)\s*(?:rooms?|accommodation):\s*(\d+)/i,
      /(\d+)\s*room/i
    ];

    for (const pattern of roomsPatterns) {
      const match = emailContent.match(pattern);
      if (match && match[1]) {
        fields.confirmedRooms = parseInt(match[1]);
        confidence += 0.1;
        break;
      }
    }

    // Extract check-in instructions
    const checkInPatterns = [
      /check[-\s]?in\s*(?:instructions?|details?|info):\s*([^\n]+)/i,
      /arrival\s*(?:instructions?|details?):\s*([^\n]+)/i
    ];

    for (const pattern of checkInPatterns) {
      const match = emailContent.match(pattern);
      if (match && match[1]) {
        fields.checkInInstructions = match[1].trim();
        confidence += 0.05;
        break;
      }
    }

    // Cap confidence at 1.0
    confidence = Math.min(confidence, 1.0);

    return { confidence, fields };
  }

  /**
   * Validate parsed data
   */
  private validateParsedData(fields: Record<string, any>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (fields.available === undefined) {
      errors.push('Availability status not found');
    }

    if (fields.available === true && !fields.confirmationNumber && !fields.totalPrice) {
      errors.push('Confirmation number or price missing for confirmed booking');
    }

    if (fields.totalPrice !== undefined && (isNaN(fields.totalPrice) || fields.totalPrice < 0)) {
      errors.push('Invalid price value');
    }

    if (fields.confirmedRooms !== undefined && (isNaN(fields.confirmedRooms) || fields.confirmedRooms < 0)) {
      errors.push('Invalid room count');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export const emailParserService = new EmailParserService();

